import React, { useState } from "react";
import { PackageCheck, Send, Undo2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function PickupCell({ order, onRefreshed }) {
  const [note, setNote] = useState(order.pickup_note || "");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const n = order.order_number;
  const status = order.pickup_status;

  const sendReady = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${n}/pickup-ready`, { note });
      toast.success(`Ready-to-collect email sent to ${data.sent_to} 🐾`);
      setOpen(false); onRefreshed?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleCollected = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/orders/${n}/picked-up`); toast.success(data.pickup_status === "collected" ? "Marked as collected 🎉" : "Back to ready"); onRefreshed?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (status === "collected") {
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid={`pickup-cell-${n}`}>
        <span className="px-2.5 py-1 rounded-full text-xs font-bold text-[#19324A] bg-[#7ED957]" data-testid={`pickup-badge-${n}`}>Collected</span>
        {order.picked_up_at && <span className="text-[11px] text-[#19324A]/50">{new Date(order.picked_up_at).toLocaleDateString("en-AU")}</span>}
        <button type="button" onClick={toggleCollected} disabled={busy} title="Undo" aria-label="Undo collected" data-testid={`pickup-undo-${n}`} className="p-1 rounded-lg hover:bg-[#19324A]/10 disabled:opacity-40"><Undo2 className="w-3.5 h-3.5 text-[#19324A]/60" /></button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 min-w-[200px]" data-testid={`pickup-cell-${n}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold text-[#19324A] ${status === "ready" ? "bg-[#4CC9F0]/40" : "bg-[#FFD93D]/40"}`} data-testid={`pickup-badge-${n}`}>{status === "ready" ? "Ready — customer emailed" : "To pack"}</span>
        {status === "ready" && (
          <button type="button" onClick={toggleCollected} disabled={busy} data-testid={`pickup-collected-${n}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#166534] hover:underline disabled:opacity-40"><PackageCheck className="w-3.5 h-3.5" />Mark collected</button>
        )}
      </div>
      {order.pickup_note && !open && <p className="text-[11px] text-[#19324A]/60 max-w-[240px] whitespace-pre-line" data-testid={`pickup-note-${n}`}>{order.pickup_note}</p>}
      {open ? (
        <div className="flex flex-col gap-1.5">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={400} placeholder="Collection time, e.g. Sat 14 Dec, 10am–2pm · 12 Example St, Slacks Creek" data-testid={`pickup-note-input-${n}`}
            className="w-full rounded-xl border-2 border-[#19324A]/10 px-2.5 py-1.5 text-xs outline-none focus:border-[#FFD93D] min-w-[220px]" />
          <div className="flex gap-2">
            <button type="button" onClick={sendReady} disabled={busy} data-testid={`pickup-send-${n}`} className="btn-primary !py-1.5 !px-3 !text-xs disabled:opacity-40"><Send className="w-3.5 h-3.5" />{busy ? "Sending…" : "Send email"}</button>
            <button type="button" onClick={() => setOpen(false)} className="text-xs font-semibold text-[#19324A]/60 hover:underline">Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)} data-testid={`pickup-ready-btn-${n}`} className="w-max inline-flex items-center gap-1.5 rounded-full bg-[#7ED957]/25 hover:bg-[#7ED957]/50 text-[#166534] text-xs font-bold px-3 py-1.5 transition-colors">
          <Send className="w-3.5 h-3.5" />{status === "ready" ? "Resend ready email" : "Ready to collect — email customer"}
        </button>
      )}
    </div>
  );
}
